import {
	IconDots,
	IconPlugConnected,
	IconRefresh,
	IconRefreshAlert,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
	useAgent,
	useDeleteAgent,
	usePingAgent,
	useRotateAgentCredential,
	useSetAgentRuntimeVersion,
	useSetAgentStatus,
	useUpdateAgent,
} from "@/common/artemis-operations/agents";
import {
	AGENT_CREDENTIAL_ROTATED_DESC,
	AGENT_CREDENTIAL_ROTATED_TITLE,
	AGENT_OFFLINE_TITLE,
	AGENT_ONLINE_DESC,
	AGENT_ONLINE_TITLE,
	AGENT_PING_PENDING,
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	ERROR_AGENT_CREDENTIAL_ROTATION_DESC,
	ERROR_AGENT_CREDENTIAL_ROTATION_TITLE,
	ERROR_AGENT_PING_DESC,
	ERROR_AGENT_PING_TITLE,
	FORCE_DELETE_AGENT_CONFIRM_LABEL,
	FORCE_DELETE_AGENT_DESC,
	FORCE_DELETE_AGENT_TITLE,
	SET_AGENT_RUNTIME_CONFIRM_LABEL,
	SET_AGENT_RUNTIME_DESC,
	SET_AGENT_RUNTIME_PENDING,
	SET_AGENT_RUNTIME_TITLE,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type { AgentCredentialReveal } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { AgentCredentialModal } from "@/components/agents/agent-credential-modal";
import { AgentDetails } from "@/components/agents/agent-details";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/agents/$agentId",
)({ component: AgentPage });

function AgentPage() {
	const { orgSlug, agentId, workspaceId } = Route.useParams();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions, setBusy } = useActions();
	const notify = useNotify();
	const {
		data: agent,
		isPending,
		isError,
		refetch,
	} = useAgent(agentId, workspaceId);
	const updateAgent = useUpdateAgent(agentId, workspaceId);
	const deleteAgent = useDeleteAgent(agentId, workspaceId);
	const { pingAgent, isPinging } = usePingAgent(agentId, workspaceId);
	const { setAgentStatus } = useSetAgentStatus(agentId, workspaceId);

	const { setAgentRuntimeVersion, isSettingRuntimeVersion } =
		useSetAgentRuntimeVersion(agentId, workspaceId);
	const { rotateAgentCredential } = useRotateAgentCredential(
		agentId,
		workspaceId,
	);
	// The one-time reveal of a freshly-rotated secret (ADR-050); gone once acknowledged.
	const [credentials, setCredentials] = useState<AgentCredentialReveal | null>(
		null,
	);
	const { confirm, dialog } = useFrictionConfirm();

	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = () => {
		const onDeleted = () => {
			notify.success(DELETE_RESOURCE_INFO_COMPLETED);
			navigate({
				to: "/$orgSlug/workspaces/$workspaceId/agents",
				params: { orgSlug, workspaceId },
			});
		};

		// Offer a force delete when the normal delete fails: the remote runtime may have been deleted
		// manually in the customer account and can't be torn down. Force removes only the StratoNext
		// record; the customer owns deleting the leftover cloud resource.
		const confirmForceDelete = () => {
			confirm({
				title: FORCE_DELETE_AGENT_TITLE,
				message: FORCE_DELETE_AGENT_DESC,
				confirmButtonLabel: FORCE_DELETE_AGENT_CONFIRM_LABEL,
				onConfirm: () => {
					deleteAgent(true, {
						onSuccess: onDeleted,
						onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
					});
				},
			});
		};

		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_RESOURCE_DESC,
			onConfirm: () => {
				deleteAgent(false, {
					onSuccess: onDeleted,
					onError: confirmForceDelete,
				});
			},
		});
	};

	// Same ref pattern as delete: keeps the toolbar effect's deps free of the confirm/rotate closures.
	const confirmRotateRef = useRef(() => {});
	confirmRotateRef.current = () =>
		// Kills the old secret immediately — same friction as other disruptive agent operations.
		confirm({
			title: "Rotate machine credential",
			message:
				"A new client secret will be generated and the current one stops working immediately. Update every machine using this credential.",
			confirmText: "rotate",
			confirmButtonLabel: "Rotate",
			onConfirm: () =>
				rotateAgentCredential(undefined, {
					onSuccess: (payload) => {
						notify.success(
							AGENT_CREDENTIAL_ROTATED_TITLE,
							AGENT_CREDENTIAL_ROTATED_DESC,
						);
						setCredentials(payload.credentials);
					},
					onError: () =>
						notify.error(
							ERROR_AGENT_CREDENTIAL_ROTATION_TITLE,
							ERROR_AGENT_CREDENTIAL_ROTATION_DESC,
						),
				}),
		});

	useEffect(() => {
		setTitle(agent?.name ?? "Agent");
	}, [setTitle, agent]);

	// Both a redeploy and a ping are slow and can outlast the default busy timeout,
	// so keep the message sticky and clear it explicitly when the operation settles.
	useEffect(() => {
		const message = isSettingRuntimeVersion
			? SET_AGENT_RUNTIME_PENDING
			: isPinging
				? AGENT_PING_PENDING
				: null;
		setBusy(message, { sticky: true });
	}, [isSettingRuntimeVersion, isPinging, setBusy]);

	useEffect(() => {
		setActions([
			...(agent?.kind === "EXTERNAL"
				? [
						{
							type: "button" as const,
							id: "rotate",
							tooltip: "Rotate secret",
							disabled: isSettingRuntimeVersion,
							icon: <CustomIcon icon={IconRefreshAlert} />,
							onClick: () => confirmRotateRef.current(),
						},
					]
				: []),
			{
				type: "button",
				id: "ping",
				tooltip: "Test connection",
				// No A2A endpoint, nothing to ping — an External agent's Ready badge comes from its
				// token heartbeat, which this button never tested anyway.
				disabled: isPinging || !agent?.endpointUrl || isSettingRuntimeVersion,
				icon: <CustomIcon icon={IconPlugConnected} />,
				onClick: () =>
					pingAgent(undefined, {
						onSuccess: (result) => {
							if (result.isHealthy) {
								notify.success(AGENT_ONLINE_TITLE, AGENT_ONLINE_DESC);
							} else {
								notify.error(
									AGENT_OFFLINE_TITLE,
									result.errorMessage ?? undefined,
								);
							}
						},
						onError: () =>
							notify.error(ERROR_AGENT_PING_TITLE, ERROR_AGENT_PING_DESC),
					}),
			},

			{
				type: "submenu",
				id: "settings",
				disabled: isSettingRuntimeVersion,
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					{
						type: "button",
						id: "delete",
						label: "Delete",
						tooltip: "Delete agent",
						danger: true,
						icon: <CustomIcon icon={IconTrash} />,
						onClick: () => confirmDeleteRef.current(),
					},
					{
						type: "button",
						id: "refresh",
						tooltip: "Refresh",
						label: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => refetch(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [
		setActions,
		refetch,
		pingAgent,
		isPinging,
		notify,
		agent?.endpointUrl,
		agent?.kind,
		isSettingRuntimeVersion,
	]);

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!agent}>
			<BasePageContainer>
				<BaseContainer
					title="Details"
					disabled={isSettingRuntimeVersion || isPinging}
				>
					<AgentCredentialModal
						credentials={credentials}
						onClose={() => setCredentials(null)}
					/>
					<AgentDetails
						agent={agent!}
						onSaveName={(name) =>
							updateAgent(
								{ name },
								{
									onSuccess: () =>
										notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
									onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
								},
							)
						}
						onSaveOwner={(ownerId) =>
							updateAgent(
								{ ownerId },
								{
									onSuccess: () =>
										notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
									onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
								},
							)
						}
						onSaveStatus={(status) =>
							setAgentStatus(status, {
								onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
								onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
							})
						}
						onSaveRuntimeVersion={(version) =>
							// Redeploys the agent's remote runtime — slow and disruptive, so it
							// gets the same friction as a delete before firing.
							confirm({
								title: SET_AGENT_RUNTIME_TITLE,
								message: SET_AGENT_RUNTIME_DESC,
								confirmText: "change",
								confirmButtonLabel: SET_AGENT_RUNTIME_CONFIRM_LABEL,
								onConfirm: () =>
									setAgentRuntimeVersion(version, {
										onSuccess: () =>
											notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
										onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
									}),
							})
						}
					/>
				</BaseContainer>
				{dialog}
			</BasePageContainer>
		</QueryGuard>
	);
}
